import React, { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { CardLink } from 'components/Link'
import isUrlExternal from 'is-url-external'
import KanBoIcon from 'components/KanBoIcon'
import CardName from 'components/CardName'
import BoardName from 'components/BoardName'
import DeskName from 'components/DeskName'
import { FormattedMessage } from 'react-intl'
import { createLocation } from 'history'
import { Route, Switch } from 'react-router-dom'

const getName = (children) => {
  if (Array.isArray(children)) {
    return children[0]
  }

  return children
}

const KanBoBoardLink = ({ children, href, boardId, viewId, onClick, ...props }) =>
  <BoardName
    id={Number(boardId)}
  >
    {({ name }) => {
      const linkName = getName(children)

      const noAccess = !name && typeof name !== 'undefined'

      let targetName
      if (linkName === href) {
        targetName = (
          <>
            <KanBoIcon
              name='kanbo-16-my-board'
              style={{
                marginRight: '0.25em',
                fontSize: 16
              }}
            />
            {noAccess ? <FormattedMessage id='LINK_KANBO_NO_ACCESS' /> : name}
          </>
        )
      } else {
        targetName = linkName
      }

      return (
        <Link
          {...props}
          to={`/board/${boardId}${viewId ? `/${viewId}` : ''}`}
          onClick={event => {
            if (noAccess) event.preventDefault()

            onClick && onClick(event)
          }}
        >
          <span
            className={noAccess ? 'text-danger' : undefined}
          >
            {targetName}
          </span>
        </Link>
      )
    }}
  </BoardName>

const KanBoCardLink = ({ children, href, cardId, onClick, ...props }) =>
  <CardName
    id={Number(cardId)}
  >
    {({ name }) => {
      const linkName = getName(children)

      const noAccess = !name && typeof name !== 'undefined'

      let targetName
      if (linkName === href) {
        targetName = (
          <>
            <KanBoIcon
              name='kanbo-16-card'
              style={{
                marginRight: '0.25em',
                fontSize: 16
              }}
            />
            {noAccess ? <FormattedMessage id='LINK_KANBO_NO_ACCESS' /> : name}
          </>
        )
      } else {
        targetName = linkName
      }

      return (
        <CardLink
          {...props}
          cardId={Number(cardId)}
          onClick={event => {
            if (noAccess) event.preventDefault()

            onClick && onClick(event)
          }}
        >
          <span
            className={noAccess ? 'text-danger' : undefined}
          >
            {targetName}
          </span>
        </CardLink>
      )
    }}
  </CardName>

const KanBoDeskLink = ({ children, href, deskId, onClick, ...props }) =>
  <DeskName
    id={Number(deskId)}
  >
    {({ name }) => {
      const linkName = getName(children)

      const noAccess = !name && typeof name !== 'undefined'

      let targetName
      if (linkName === href) {
        targetName = (
          <>
            <KanBoIcon
              name='kanbo-16-desk'
              style={{
                marginRight: '0.25em',
                fontSize: 16
              }}
            />
            {noAccess ? <FormattedMessage id='LINK_KANBO_NO_ACCESS' /> : name}
          </>
        )
      } else {
        targetName = linkName
      }

      return (
        <Link
          {...props}
          to={`/home/${deskId}`}
          onClick={event => {
            if (noAccess) event.preventDefault()

            onClick && onClick(event)
          }}
        >
          <span
            className={noAccess ? 'text-danger' : undefined}
          >
            {targetName}
          </span>
        </Link>
      )
    }}
  </DeskName>

const KanBoMyBoardLink = ({ children, href, userId, ...props }) => {
  const linkName = getName(children)

  return (
    <Link
      {...props}
      to={`/myboard/${userId}`}
    >
      {linkName === href ? (
        <>
          <KanBoIcon
            name='kanbo-16-my-board-2'
            style={{
              marginRight: '0.25em',
              fontSize: 16
            }}
          />
          {linkName}
        </>
      ) : linkName}
    </Link>
  )
}

const KanBoLink = ({ children, href, ...props }) => {
  const location = useLocation()

  const switchLocation = useMemo(() => {
    try {
      return createLocation(new URL(href).pathname)
    } catch {
      return null
    }
  }, [href])

  if (!href) {
    return (
      <a {...props}>
        {children}
      </a>
    )
  }

  let urlExternal

  try {
    urlExternal = isUrlExternal(href)
  } catch (e) {
    urlExternal = true
  }

  if (urlExternal || !switchLocation) {
    return (
      <a
        {...props}
        href={href}
        target={'_blank'}
        rel={'noopener noreferrer'}
      >
        {children}
      </a>
    )
  }

  return (
    <Switch
      location={switchLocation}
    >
      <Route
        exact
        path={['/board/:boardId(\\d+)', '/board/:boardId(\\d+)/:viewId']}
      >
        {({ match }) =>
          <Route
            location={location}
          >
            <KanBoBoardLink
              {...props}
              href={href}
              boardId={match.params.boardId}
              viewId={match.params.viewId}
            >
              {children}
            </KanBoBoardLink>
          </Route>
        }
      </Route>
      <Route
        exact
        path={'/home/:id(\\d+)'}
      >
        {({ match }) =>
          <Route
            location={location}
          >
            <KanBoDeskLink
              {...props}
              href={href}
              deskId={match.params.id}
            >
              {children}
            </KanBoDeskLink>
          </Route>
        }
      </Route>
      <Route
        exact
        path={'/myboard/:userId(\\d+)'}
      >
        {({ match }) =>
          <Route
            location={location}
          >
            <KanBoMyBoardLink
              {...props}
              href={href}
              userId={match.params.userId}
            >
              {children}
            </KanBoMyBoardLink>
          </Route>
        }
      </Route>
      <Route path={'/card/:cardId(\\d+)'}>
        {({ match }) =>
          <Route
            location={location}
          >
            <KanBoCardLink
              {...props}
              href={href}
              cardId={match.params.cardId}
            >
              {children}
            </KanBoCardLink>
          </Route>
        }
      </Route>
      <Route
        location={location}
      >
        <a
          {...props}
          href={href}
        >
          {children}
        </a>
      </Route>
    </Switch>
  )
}

export default KanBoLink
